import { ReactNode, useEffect } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { insuranceSelector } from '../../model';
import { useSelector } from 'react-redux';
import { SubmissionDetails } from '../SubmissionDetails';
import { FormProvider, useForm } from 'react-hook-form';
import { InsuranceSelectSubmissionForm } from '../../types';
import { InsuranceSelectSubmissionDialog } from '../dialogs';
import { ModalsContainer } from 'components/shared/Modals';
import { ReadOnlyQuill } from 'modules/desking/ui/components/catalogDetails/ReadOnlyQuill';
import { checkIfQuillValueIsEmpty, convertHtmlToDelta } from 'utils/quillUtils';

export const LenderInsuranceRequirements = () => {
  const classes = useStyles();

  const modalsWithKeys: Partial<Record<ModalsKeys, ReactNode>> = {
    [ModalsKeys.InsuranceSelectSubmissionDialog]: <InsuranceSelectSubmissionDialog />,
  };

  const { handleOpen: handleOpenSubmissionDialog } = useModal(ModalsKeys.InsuranceSelectSubmissionDialog);

  const selectedSubmission = useSelector(insuranceSelector.selectedSubmission);
  const selectedFinanceProgram = useSelector(insuranceSelector.selectedFinanceProgram);

  const form = useForm<InsuranceSelectSubmissionForm>({
    defaultValues: {
      submissionId: '',
      financeProgramId: '',

      coverageRequirements: '',
      certificateRequirements: '',
      otherInstructions: '',
    },
  });

  // TODO: think of better approach on set initial form values (when task has been already created)
  useEffect(() => {
    form.reset({
      submissionId: selectedSubmission?.creditSubmissionId || '',
      financeProgramId: selectedFinanceProgram?.financeProgramId || '',
      coverageRequirements: selectedFinanceProgram?.insuranceCoverage?.coverageRequirements || '',
      certificateRequirements: selectedFinanceProgram?.insuranceCoverage?.certificateRequirements || '',
      otherInstructions: selectedFinanceProgram?.insuranceCoverage?.otherInstructions || '',
    });
  }, [selectedSubmission, selectedFinanceProgram]);

  return (
    <Box className={classes.root}>
      <FormProvider {...form}>
        {
          /* stepperConfig[0].completed */ selectedFinanceProgram ? (
            <Stack gap="1rem">
              <Button
                size="small"
                color="primary"
                sx={{ alignSelf: 'flex-start' }}
                onClick={() => {
                  handleOpenSubmissionDialog();
                }}
              >
                Change Submission
              </Button>
              <Typography component="span" variant="h6">
                {selectedSubmission!.lenderName}
              </Typography>
              <SubmissionDetails selectedSubmission={selectedSubmission!} />

              {selectedFinanceProgram?.insuranceCoverage?.coverageRequirements &&
                !checkIfQuillValueIsEmpty(selectedFinanceProgram?.insuranceCoverage?.coverageRequirements) && (
                  <Box>
                    <Typography component="span" variant="subtitle2">
                      Coverage Requirements:
                    </Typography>

                    <ReadOnlyQuill
                      value={convertHtmlToDelta(selectedFinanceProgram.insuranceCoverage.coverageRequirements)}
                    />
                  </Box>
                )}

              {selectedFinanceProgram?.insuranceCoverage?.certificateRequirements &&
                !checkIfQuillValueIsEmpty(selectedFinanceProgram?.insuranceCoverage?.certificateRequirements) && (
                  <Box>
                    <Typography component="span" variant="subtitle2">
                      Certificate Requirements:
                    </Typography>

                    <ReadOnlyQuill
                      value={convertHtmlToDelta(selectedFinanceProgram.insuranceCoverage.certificateRequirements)}
                    />
                  </Box>
                )}

              {selectedFinanceProgram?.insuranceCoverage?.otherInstructions &&
                !checkIfQuillValueIsEmpty(selectedFinanceProgram?.insuranceCoverage?.otherInstructions) && (
                  <Box>
                    <Typography component="span" variant="subtitle2">
                      Other Instructions:
                    </Typography>

                    <ReadOnlyQuill
                      value={convertHtmlToDelta(selectedFinanceProgram.insuranceCoverage.otherInstructions!)}
                    />
                  </Box>
                )}
            </Stack>
          ) : (
            <Button
              size="small"
              color="primary"
              variant="contained"
              onClick={() => {
                handleOpenSubmissionDialog();
              }}
            >
              Select Submission
            </Button>
          )
        }

        <ModalsContainer modalsWithKeys={modalsWithKeys} />
      </FormProvider>
    </Box>
  );
};

const useStyles = makeStyles({
  root: {
    backgroundColor: '#FAFAFA',
    padding: '24px',
  },
});
